
import { Component, Vue, Prop } from "vue-property-decorator";

import { Config, Logger, LogLevel } from "../services";
import { VideoRecorderField } from "../types/Forms/FieldTypes";

interface VideoCaptureConfig {
  codec: string;
}

@Component
export default class VideoRecorder extends Vue {
  @Prop()
  private readonly def!: VideoRecorderField;

  private isActive = false;

  private ready = false;

  private needsFallback = false;

  private get isMobile(): boolean {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  private needsPermissions = false;

  private hasNoDevices = false;

  private permissionDenied = false;

  private hasFatalError = false;
  
  private fatalErrorMessage = '';

  private isRecording = false;

  private videoBlob: Blob | null = null;

  private outatimeThreshold: number | null = null;

  private captureConfig: VideoCaptureConfig | null = null;

  private currentTime = 0;

  private currentTimeInterval: number | null = null;

  private videoStream: MediaStream | null = null;

  private mediaRecorder: MediaRecorder | null = null;

  private capturedChunks: Blob[] = [];

  get formattedTime(): string {
    let mins: string | number = Math.floor(this.currentTime / 60);
    let secs: string | number = this.currentTime - (mins * 60);

    let totalMins: string | number = Math.floor(this.def.MaxRecordLength / 60);
    let totalSecs: string | number = this.def.MaxRecordLength - (totalMins * 60);

    if (secs < 10) secs = "0" + secs;
    if (totalSecs < 10) totalSecs = "0" + totalSecs;

    return mins + ":" + secs + " / " + totalMins + ":" + totalSecs;
  }

  get isRunningOutatime(): boolean {
    return this.currentTime >= this.def.MaxRecordLength * 0.75;
  }

  mounted() {

    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) === false) {
      Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Falling back to upload on a non-mobile device.');

      this.fallback();
      return;
    }

    if ('MediaRecorder' in window && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      Logger.Log(LogLevel.Debug, '[Video Recorder]', 'MediaRecorder, enumerateDevices are supported');

      navigator.mediaDevices.enumerateDevices().then((availableDevices) => {

        if (availableDevices.some((device) => device.kind === 'videoinput')) {

          // Check that the permissions API is supported
          if('permissions' in navigator) {
            Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Attempting to determine if camera permissions are granted');

            try {
              // Check to see if we have permission to use the camera
              navigator.permissions.query({ name: "camera" }).then(res => {
                if(res.state !== 'granted') {
                  Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Camera permissions not yet granted');
                  
                  // If the state is not granted, we need to ask for permissions
                  this.ready = true;
                  this.needsPermissions = true;
                } else {
                  Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Camera permissions granted');

                  this.ready = true;
                }
              });
            } catch (ex) {
              Logger.Log(LogLevel.Warning, '[Video Recorder]', 'Failed to get camera permissions state', ex);

              // Error checking for permissions
              this.needsPermissions = true;
            }
          } else {
            Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Permissions API not supported. Requesting permissions manually.');

            // The permissions API isn't supported
            this.needsPermissions = true;
          }

        } else {
          Logger.Log(LogLevel.Debug, '[Video Recorder]', 'No videoinput devices are available. Falling back to traditional methods.');
          this.fallback();
        }

      }).catch((err) => {
        Logger.Log(LogLevel.Error, '[Video Recorder]', 'Failed to enumerate media devices', err);

        this.fallback();
      });
    } else {
      Logger.Log(LogLevel.Debug, '[Video Recorder]', 'MediaRecorder is not supported, falling back to traditional upload mechanisms');

      this.fallback();
    }
  }

  private toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  private initCaptureConfig(): void {
    const codecs = [
        "video/mp4",
        "video/mpeg",
        "video/webm;codecs=h264",
        "video/webm",
        "video/webm;codecs=vp8",
        "video/webm;codecs=daala"
    ];

    this.captureConfig = {
      codec: ''
    };

    for (var i = 0; i < codecs.length; i += 1) {
      if (MediaRecorder.isTypeSupported(codecs[i])) {
        this.captureConfig.codec = codecs[i];
        break;
      }
    }

    if (this.captureConfig.codec.length === 0) {
      throw new Error('Unable to find a suitable codec.');
    }

    Logger.Log(LogLevel.Debug, '[Video Recorder]', `Using codec ${codecs[i]}`)
  }

  private startRecording() {
    if (this.isRecording) return;

    this.isRecording = true;

    this.initVideoStream().then(() => {
      this.capturedChunks = [];

      Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Start Recording');

      if (this.mediaRecorder === null) {

        if (this.captureConfig === null) {
          try {
            this.initCaptureConfig();
          } catch (ex) {
            Logger.Log(LogLevel.Error, '[Video Recorder]', 'Failed to initialise capture config.');
            
            this.fallback();

            return;
          }
        }

        this.mediaRecorder = new MediaRecorder(this.videoStream!, {
          mimeType: this.captureConfig!.codec
        });

        this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
          this.capturedChunks.push(e.data);
        }

        this.mediaRecorder.onstop = () => {
          this.videoBlob = new Blob(this.capturedChunks, { type: this.captureConfig!.codec });

          this.isRecording = false;

          window.clearInterval(this.currentTimeInterval!);
          this.currentTime = 0;

          Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Stopped recording');

          this.doneRecording();
        }

        Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Initialised MediaRecorder', this.mediaRecorder);
      }

      this.currentTime = 0;

      this.mediaRecorder.start();

      this.currentTimeInterval = window.setInterval(() => {
        this.currentTime += 1;
        
        if (this.currentTime >= this.def.MaxRecordLength) {
          Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Outatime 🔥 (stopping recording)');
          this.stopRecording();
        }
      }, 1000);
    });
  }

  private stopRecording(): void {
    this.mediaRecorder!.stop();
  }

  private doneRecording(): void {
    this.hideRecorder();

    this.$emit('done', this.videoBlob);
  }

  private cancelRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    }

    this.doneRecording();
  }

  private createFileInput(): HTMLInputElement {
    const fi = document.createElement('input');

    fi.type = 'file';
    fi.accept = 'video/mp4';

    fi.setAttribute('capture', 'environment');

    fi.style.display = 'none';

    return fi as HTMLInputElement;
  }

  private fallback(): void {
    this.ready = false;
    this.needsFallback = true;
  }

  private uploadVideo(): void {
    const fi = this.createFileInput();

    fi.onchange = () => {
      Logger.Log(LogLevel.Debug, '[Video Recorder]', 'File value changed', fi.files);

      if (fi.files!.length) {
        const file = fi.files![0];

        if(file.size > this.def.MaxFileSize) {
          alert(`The selected file excedes the maximum allowable file size of ${this.def.FormattedMaxFileSize}. Please select a different video to continue.`);
        } else {
          this.videoBlob = fi.files![0];

          this.doneRecording();
        }
        
        fi.remove();
      }
    };

    fi.onblur = () => {
      setTimeout(() => {
        
        Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Removing file input node');

        fi.remove();
      }, 100);
    }

    document.body.appendChild(fi);

    fi.click();
  }

  private hideRecorder(): void {
    if (this.videoStream !== null) {
      this.videoStream!.getTracks().forEach(function(track) {
        track.stop();
      });

      this.videoStream = null;
      this.mediaRecorder = null;
    }

    this.isActive = false;
  }

  private showRecorder(): void {
    this.initVideoStream().then(() => {
      this.isActive = true;
    }).catch(() => {
      this.needsFallback = true;
    });
  }

  private initVideoStream(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.videoStream !== null) {
        this.needsPermissions = false;
        resolve();
        return;
      }

      const constraints = { audio: true, video: { width: 1280, height: 720 } }; 

      navigator.mediaDevices.getUserMedia(constraints).then((mediaStream) => {

        Logger.Log(LogLevel.Debug, '[Video Recorder]', 'Successfully obtained MediaStream');

        this.ready = true;

        this.videoStream = mediaStream;

        this.needsPermissions = false;

        (this.$refs.preview as HTMLVideoElement).srcObject = this.videoStream;

        resolve();

      }).catch((ex) => {

        Logger.Log(LogLevel.Error, '[Video Recorder]', 'Failed to obtain MediaStream', ex.name, ex.message);

        switch (ex.name) {
          case "NotFoundError":

            this.hasNoDevices = true;

            // Fall back to requesting a normal upload
            this.fallback();

            break;
          case "NotAllowedError":

            this.permissionDenied = true;

            break;
        }

        reject();
      });
    });
  }

  requestRecording(): void {
    if (this.needsFallback) {
      this.uploadVideo();
    } else {
      this.showRecorder();
    }
  }
}
